import { COVER_PLACEHOLDER_SRC } from '../../../shared/config/constants'
import { t } from '../../../shared/config/translations'
import type {
	ClientRelease,
	ReleaseHistory,
} from '../../../shared/types/collection'
import { getCollectionStoreState } from '../../stores/collectionStore'
import type { OverlayOptions, OverlayView } from '../Overlay'
import { limitOverlayValidity, pushOverlayContent } from '../Overlay'
import {
	type PreviewQueue,
	playTrackPreview,
	prepareTrackPreviews,
	syncPreviewTrackRows,
} from '../PreviewPlayer'
import { showSnackbar } from '../Snackbar'
import {
	handleListenButtonClick,
	hydrateRelease,
	initializeReleaseDetails,
} from './ReleaseDetails'
import { handleEditButtonClick } from './ReleaseEditDialog'
import { initializeReleaseFavorite } from './ReleaseFavorite'
import { initializeReleaseRating } from './ReleaseRating'
import {
	renderHistoryList,
	renderReleaseInfoList,
	renderTracklist,
} from './ReleaseRenderers'
import { initReleaseSimilarItems } from './ReleaseSimilarItems'
import {
	releaseHeaderRatingTemplate,
	releaseRatingEditorTemplate,
} from './releaseRatingTemplates'
import {
	releaseLazyTemplate,
	releaseTemplate,
	releaseYearLoadingTemplate,
} from './releaseTemplates'

export { updateReleaseDetails } from './ReleaseDetails'

type ReleaseTab = 'history' | 'details'

const COVER_PLACEHOLDER = COVER_PLACEHOLDER_SRC

class ReleaseController {
	private readonly rootEl: HTMLElement
	private readonly tabListEl: HTMLElement | null
	private readonly tabButtons: HTMLButtonElement[]
	private readonly tabPanels: HTMLElement[]
	private readonly listenButtonEl: HTMLButtonElement | null
	private readonly editButtonEl: HTMLButtonElement | null
	private release: ClientRelease
	private previewQueue: PreviewQueue
	private readonly readOnly: boolean

	constructor(rootEl: HTMLElement, release: ClientRelease) {
		this.rootEl = rootEl
		this.tabListEl = rootEl.querySelector<HTMLElement>('.tab-list')
		this.tabButtons = Array.from(
			rootEl.querySelectorAll<HTMLButtonElement>(
				'[data-tab="history"], [data-tab="details"]',
			),
		)
		this.tabPanels = Array.from(
			rootEl.querySelectorAll<HTMLElement>('[data-tab-panel]'),
		)
		this.listenButtonEl =
			rootEl.querySelector<HTMLButtonElement>('#listen-button')
		this.editButtonEl =
			rootEl.querySelector<HTMLButtonElement>('#edit-button')
		this.release = release
		this.previewQueue = createPreviewQueue(release)
		this.readOnly = rootEl.dataset.readOnly === 'true'

		this.handleTabClick = this.handleTabClick.bind(this)
		this.handleListenClick = this.handleListenClick.bind(this)
		this.handleEditClick = this.handleEditClick.bind(this)
		this.handleInfoCopyClick = this.handleInfoCopyClick.bind(this)
		this.handleTrackPreviewClick = this.handleTrackPreviewClick.bind(this)
		this.handleReleaseDetailsUpdated =
			this.handleReleaseDetailsUpdated.bind(this)
		this.init()
	}
	private init(): void {
		limitOverlayValidity(Date.parse(this.release.data.expiresAt ?? ''))
		initializeReleaseDetails(this.release, this.rootEl)
		if (!this.readOnly) {
			initializeReleaseFavorite(this.rootEl, this.release)
			initializeReleaseRating(this.rootEl, this.release)
			initReleaseSimilarItems(this.rootEl, this.release, (release) => {
				pushOverlayContent(renderRelease(release))
			})
		}
		if (this.previewQueue.tracks.length > 0) {
			prepareTrackPreviews(this.previewQueue)
		}
		this.setActiveTab('history')
		this.addEventListeners()
	}
	private addEventListeners(): void {
		this.tabButtons.forEach((button) => {
			button.addEventListener('click', this.handleTabClick)
		})

		this.listenButtonEl?.addEventListener('click', this.handleListenClick)
		this.editButtonEl?.addEventListener('click', this.handleEditClick)
		this.rootEl.addEventListener('click', this.handleInfoCopyClick)
		this.rootEl.addEventListener('click', this.handleTrackPreviewClick)
		this.rootEl.addEventListener(
			'release:details-updated',
			this.handleReleaseDetailsUpdated,
		)
		syncPreviewTrackRows()
	}

	private handleTabClick(event: Event): void {
		const target = event.currentTarget
		if (!(target instanceof HTMLButtonElement)) return
		const nextTab =
			(target.dataset.tab as ReleaseTab | undefined) ?? 'history'
		this.setActiveTab(nextTab)
	}

	private handleListenClick(): void {
		void handleListenButtonClick(this.release)
	}

	private handleEditClick(): void {
		void handleEditButtonClick(this.release)
	}

	private async handleInfoCopyClick(event: Event): Promise<void> {
		const target = event.target
		if (!(target instanceof Element)) return

		const button = target.closest<HTMLButtonElement>('[data-copy-value]')
		const copyValue = button?.dataset.copyValue
		if (!button || !copyValue) return

		try {
			await navigator.clipboard.writeText(copyValue)
			const feedback = button.parentElement?.querySelector(
				'.release-info-copy-feedback',
			)
			if (!feedback || !button.isConnected) return
			const label = document.createElement('span')
			label.textContent = 'Copied'
			label.addEventListener('animationend', () => label.remove(), {
				once: true,
			})
			feedback.replaceChildren(label)
		} catch (error) {
			console.error('Failed to copy release info value', error)
			showSnackbar('Could not copy Discogs ID', 'error')
		}
	}

	private handleTrackPreviewClick(event: Event): void {
		const target = event.target
		if (!(target instanceof Element)) return
		const button = target.closest<HTMLButtonElement>(
			'[data-preview-track-index]',
		)
		const trackIndex = Number(button?.dataset.previewTrackIndex)
		const tracks = this.previewQueue.tracks
		if (!button || !Number.isInteger(trackIndex) || !tracks?.[trackIndex]) {
			return
		}

		playTrackPreview(this.previewQueue, trackIndex)
	}

	private handleReleaseDetailsUpdated(event: Event): void {
		const release = (event as CustomEvent<ClientRelease>).detail
		if (!release?.data || release.data.id !== this.release.data.id) return
		this.release = release
		this.previewQueue = createPreviewQueue(release)
		if (this.previewQueue.tracks.length > 0) {
			prepareTrackPreviews(this.previewQueue)
		}
	}
	private setActiveTab(activeTab: ReleaseTab): void {
		this.tabPanels.forEach((panel) => {
			const isActive = panel.dataset.tabPanel === activeTab
			panel.classList.toggle('active', isActive)
			panel.toggleAttribute('hidden', !isActive)
		})

		this.tabButtons.forEach((button) => {
			const isActive = button.dataset.tab === activeTab
			button.setAttribute('aria-selected', String(isActive))
			button.setAttribute('aria-expanded', String(isActive))
		})

		if (this.tabListEl) this.tabListEl.dataset.activeTab = activeTab
	}
}

function createPreviewQueue(release: ClientRelease): PreviewQueue {
	return {
		artist: release.data.artists
			.map((artist) => artist.name)
			.filter(Boolean)
			.join(', '),
		releaseId: release.data.id,
		releaseTitle: release.data.title,
		releaseYear: release.data.master_year || release.data.year,
		tracks: release.data.tracklist ?? [],
	}
}

function bindReleaseEvents(release: ClientRelease): void {
	const releaseEl = document.getElementById('release')
	if (!releaseEl) return
	new ReleaseController(releaseEl, release)
}

export function renderReleaseLazy(
	releaseId: number,
	instanceId?: number,
	fallback?: {
		date_added?: string
		history?: ReleaseHistory | null
		isFavorite?: boolean
		rating?: number | null
		preview?: {
			artists: string
			coverSrc: string
			isHidden: boolean
			title: string
			year: string
		}
	},
	options?: OverlayOptions,
	readOnly = false,
): OverlayView {
	const preview = fallback?.preview
	const headerClassName = preview?.isHidden ? 'is-hidden-release' : ''
	const headerRating = readOnly ? null : (fallback?.rating ?? null)
	options = {
		...options,
		expiresAt:
			options?.expiresAt ??
			Date.parse(getCollectionStoreState().status?.validUntil ?? ''),
	}
	return {
		path: `/collection/${releaseId}`,
		html: releaseLazyTemplate(
			releaseId,
			instanceId,
			preview
				? {
						artists: preview.artists,
						coverPlaceholderSrc: COVER_PLACEHOLDER,
						coverSrc: preview.coverSrc,
						headerClassName,
						headerRatingHtml: releaseHeaderRatingTemplate(
							headerRating,
							!!preview.year,
						),
						title: preview.title,
						year: preview.year,
					}
				: undefined,
		),
		onMount: () => {
			void hydrateRelease(releaseId, instanceId, fallback, (release) =>
				renderRelease(release, false, options, readOnly),
			)
		},
		options,
	}
}

export function renderRelease(
	release: ClientRelease,
	dimmed = false,
	options: OverlayOptions = {
		link: {
			text: t('nav.showOnDiscogs'),
			url: release.data.uri,
		},
	},
	readOnly = false,
): OverlayView {
	const history = release.history
	const data = release.data
	const coverSrc = data.cover_image || COVER_PLACEHOLDER
	let year = ''
	if (data.master_year && data.master_year > 0) {
		year = String(data.master_year)
	} else if (data.master_year_status === 'loading') {
		year = releaseYearLoadingTemplate(t('release.loading'))
	}
	const isDimmed =
		dimmed || !!release.history?.isHidden || !!release.history?.hiddenUntil

	return {
		path: `/collection/${data.id}`,
		onMount: () => {
			requestAnimationFrame(() => {
				const header = document.getElementById('release-header')
				if (header) {
					const height = header.getBoundingClientRect().height
					document.documentElement.style.setProperty(
						'--release-header-height',
						`${height}px`,
					)
				}
			})
			bindReleaseEvents(release)
		},
		options,
		html: releaseTemplate({
			releaseId: data.id,
			releaseInstanceId: release.id,
			headerClassName: isDimmed ? 'is-hidden-release' : '',
			title: data.title,
			artists: release.data.artists
				.map((artist) => artist.name)
				.filter(Boolean)
				.join(', '),
			year,
			coverSrc,
			coverPlaceholderSrc: COVER_PLACEHOLDER,
			headerRatingHtml: releaseHeaderRatingTemplate(
				readOnly ? null : release.rating,
				!!year,
			),
			ratingEditorHtml: releaseRatingEditorTemplate(release.rating),
			editRatingLabel: t('release.editRating'),
			listenButtonText: t('release.listen'),
			editButtonText: t('release.edit'),
			favoriteButtonLabel: t(
				release.isFavorite
					? 'release.removeFavorite'
					: 'release.addFavorite',
			),
			isFavorite: !!release.isFavorite,
			releaseInfosLabel: t('release.releaseInfos'),
			tracklistLabel: t('release.tracklist'),
			historyHtml: renderHistoryList(history, release.date_added),
			releaseInfoHtml: renderReleaseInfoList(release),
			tracklistHtml: renderTracklist(data),
			loadingLabel: t('release.loading'),
			retryLabel: t('release.retry'),
			loadLabel: t('release.load'),
			readOnly,
		}),
	}
}
